import React from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '../../hooks/useScrollReveal';
import SectionHeader from '../ui/SectionHeader';

interface Persona {
  icon: string;
  colorClass: string;
  title: string;
  intro: string;
  bullets: string[];
}

const personas: Persona[] = [
  {
    icon: '📊',
    colorClass: 'ic-teal',
    title: 'Head of Sustainability / ESG Manager',
    intro: '"Stop managing data collection in spreadsheets."',
    bullets: [
      'Structured collection process with named data owners and framework-mapped questions',
      'Timeline you can enforce — across every entity and plant in your organisation',
      'Audit trail that tells the story of every data point, from submission to sign-off',
    ],
  },
  {
    icon: '📈',
    colorClass: 'ic-amber',
    title: 'CFO / Finance Director',
    intro: '"EU Taxonomy KPIs and CBAM certificate costs live inside your sustainability report."',
    bullets: [
      'Taxonomy KPIs calculated from activity data — revenue, CapEx, and OpEx ratios',
      'Disclosure tables produced in the format required under the Taxonomy Disclosures Delegated Act',
      'CBAM certificate cost modelling so finance sees the exposure before the invoice arrives',
    ],
  },
  {
    icon: '🔗',
    colorClass: 'ic-emerald',
    title: 'Chief Procurement Officer',
    intro: '"CBAM, Scope 3 Category 1, and supplier ESG data all start in your supply chain."',
    bullets: [
      'Sustainability data integrated into SAP Ariba sourcing events and supplier onboarding',
      'Supplier sustainability scores visible where procurement decisions are made',
      'Supplier carbon data feeds your corporate carbon footprint automatically — no double collection',
    ],
  },
  {
    icon: '🛡️',
    colorClass: 'ic-navy',
    title: 'Internal Audit / Compliance',
    intro: '"Every data point is logged, timestamped, and traceable."',
    bullets: [
      'Complete audit trail for CSRD disclosures, CBAM declarations, and Taxonomy alignment evidence',
      'Submission, revision, approval, and sign-off captured with identity and timestamp',
      'Export a complete evidence package — not a collection of emails and spreadsheet versions',
    ],
  },
];

export default function Personas(): React.ReactElement {
  const ref = useScrollReveal();

  return (
    <section
      className="personas section-pad"
      id="personas"
      ref={ref as React.RefObject<HTMLElement>}
    >
      <div className="container">
        <SectionHeader
          eyebrow="Who Actula is built for"
          title="The right tool for every stakeholder."
          description="Sustainability reporting touches procurement, finance, audit, and the ESG team. Actula is designed for all of them."
        />
        <div className="personas-grid">
          {personas.map((p, i) => (
            <div key={i} className={`persona-card reveal reveal-delay-${(i % 4) + 1}`}>
              <div className="persona-icon-row">
                <div className={`icon-circle icon-circle-md ${p.colorClass}`}>
                  <span style={{ fontSize: '1.25rem' }}>{p.icon}</span>
                </div>
                <p className="persona-title">{p.title}</p>
              </div>
              <p className="persona-intro">{p.intro}</p>
              <ul className="persona-bullets">
                {p.bullets.map((b, j) => (
                  <li key={j} className="persona-bullet">{b}</li>
                ))}
              </ul>
              <Link to="/contact" className="link-arrow">Get started</Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
